import ErrorFilledIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Error-Filled-Lg.js';
import WarningIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Warning.js';
import { Box, BoxProps, Button, Divider, useTheme } from '@mui/material';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import type { Theme } from '@mui/material/styles';
import { forwardRef } from 'react';
import {
  atlasFontTextBody,
  atlasRadiusMd,
  atlasSpacing,
  atlasToastAlertContainerSx,
  atlasToastAlertIconStripSx,
  type AtlasLensTheme,
} from '../Form/atlasLensStyles';
import { resolveAtlasLensColors } from '../Form/formFieldSpec';
import { ATLAS_LENS_SEMANTIC_VARS } from '../Form/atlasLensTokens';

export type AtlasErrorActionToastProps = {
  message: string;
  dismissLabel: string;
  onDismiss: () => void;
  actionLabel?: string;
  onAction?: () => void;
  /** `warning` — yellow strip + 480px width (Figma sync-logo failure 19041:271670). */
  severity?: 'error' | 'warning';
} & Omit<BoxProps, 'children'>;

/** Figma action toast — dark surface, status accent strip, message, Dismiss / action. */
export const AtlasErrorActionToast = forwardRef<HTMLDivElement, AtlasErrorActionToastProps>(
  function AtlasErrorActionToast(
    { message, dismissLabel, onDismiss, actionLabel, onAction, severity = 'error', ...rest },
    ref,
  ) {
    const theme = useTheme() as Theme & LensTheme;
    const atlasTheme = theme as AtlasLensTheme;
    const { tokens } = theme;
    const { toastSurface, accentRedRequired, typeInverse } = resolveAtlasLensColors(theme);
    const borderColor =
      tokens?.semantic?.color?.ui?.divider?.default?.value ??
      ATLAS_LENS_SEMANTIC_VARS.dividerDefault;
    const isWarning = severity === 'warning';
    const stripColor = isWarning
      ? (tokens?.semantic?.color?.status?.warning?.default?.value ?? '#fee400')
      : accentRedRequired;
    const iconColor = isWarning ? '#1a1a1a' : typeInverse;

    return (
      <Box
        ref={ref}
        {...rest}
        role="alert"
        aria-live={isWarning ? 'polite' : 'assertive'}
        data-qa-anchor={
          isWarning ? 'atlas-toast-warning-action' : 'atlas-toast-error-action'
        }
        sx={{
          ...atlasToastAlertContainerSx(atlasTheme),
          // Figma is 480px on desktop; shrink inside narrow Snackbar / zoomed viewports.
          ...(isWarning
            ? { width: '100%', maxWidth: 480, minWidth: 0, boxSizing: 'border-box' }
            : null),
          background: toastSurface,
          border: `1px solid ${borderColor}`,
        }}
      >
        <Box
          sx={{
            ...atlasToastAlertIconStripSx(atlasTheme),
            ...(isWarning ? { width: 40, minWidth: 40 } : null),
            background: stripColor,
          }}
        >
          <Box sx={{ display: 'flex', color: iconColor }}>
            {isWarning ? <WarningIcon /> : <ErrorFilledIcon />}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: `${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '2')}`,
            gap: atlasSpacing(atlasTheme, '1'),
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              padding: `${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '2')} ${atlasSpacing(atlasTheme, '2')}`,
              alignSelf: 'stretch',
            }}
          >
            <Box
              sx={{
                ...atlasFontTextBody(atlasTheme),
                color: typeInverse,
                flex: 1,
              }}
            >
              {message}
            </Box>
          </Box>
          <Divider
            sx={{
              width: '100%',
              borderColor,
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: `${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '1')} 0`,
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Button
              variant="text"
              size="medium"
              onClick={onDismiss}
              sx={{
                color: typeInverse,
                borderRadius: atlasRadiusMd(atlasTheme),
                textTransform: 'none',
                fontWeight: tokens?.core?.fontWeight?.semiBold?.value ?? 600,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
              }}
            >
              {dismissLabel}
            </Button>
            {actionLabel && onAction ? (
              <Button
                variant="text"
                size="medium"
                onClick={onAction}
                sx={{
                  color: typeInverse,
                  borderRadius: atlasRadiusMd(atlasTheme),
                  textTransform: 'none',
                  fontWeight: tokens?.core?.fontWeight?.semiBold?.value ?? 600,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                {actionLabel}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
    );
  },
);
